import React, { useState } from 'react';
import { Badge } from 'flowbite-react';
import { HiMail, HiPhone, HiPencil, HiTrash, HiCheck, HiDuplicate } from 'react-icons/hi';

interface ContactBadgeProps {
  type: 'email' | 'phone';
  value: string;
  extension?: string | null;
  isPrimary?: boolean;
  typeName?: string | null;
  onEdit?: () => void;
  onDelete?: () => void;
  canEdit?: boolean;
}

export const ContactBadge: React.FC<ContactBadgeProps> = ({
  type,
  value,
  extension,
  isPrimary,
  typeName,
  onEdit,
  onDelete,
  canEdit = false
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopyToClipboard = () => {
    const textToCopy = type === 'phone' && extension ? `${value} ext ${extension}` : value;
    navigator.clipboard.writeText(textToCopy).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const Icon = type === 'email' ? HiMail : HiPhone;

  return (
    <div
      className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-full transition-colors group"
    >
      {/* Icon and value section - clickable to copy */}
      <div
        className="flex items-center gap-2 cursor-pointer"
        onClick={handleCopyToClipboard}
        title={`Click to copy ${type}`}
      >
        <Icon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
        <span className="text-sm text-gray-800 dark:text-gray-200">
          {value}
          {extension && (
            <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">ext {extension}</span>
          )}
        </span>

        {/* Copy indicator */}
        <div className="ml-1">
          {copied ? (
            <HiCheck className="w-3.5 h-3.5 text-green-600 dark:text-green-400" />
          ) : (
            <HiDuplicate className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity" />
          )}
        </div>
      </div>

      {/* Type name badge */}
      {typeName && (
        <Badge size="xs" color="gray" className="px-1.5 py-0.5">
          {typeName}
        </Badge>
      )}

      {/* Primary indicator */}
      {isPrimary && (
        <Badge size="xs" color="info" className="px-1.5 py-0.5">
          Primary
        </Badge>
      )}

      {/* Edit/Delete buttons - only show if user can edit */}
      {canEdit && (
        <div className="flex items-center gap-1 ml-1 border-l border-gray-300 dark:border-gray-600 pl-2">
          {onEdit && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              className="p-1 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              title="Edit"
              aria-label={`Edit ${type}`}
            >
              <HiPencil className="w-3.5 h-3.5" />
            </button>
          )}
          {onDelete && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              className="p-1 text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
              title="Delete"
              aria-label={`Delete ${type}`}
            >
              <HiTrash className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};